/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {Fragment, useEffect, useMemo, useState} from 'react'
import {Button, makeStyles} from '@material-ui/core'
import DetailProductOrderDialog from 'components/Dialog/Product/DetailProductOrderDialog'
import ProductsOrderStyle from 'pages/ProductOrderPages/style'
import {useTranslation} from 'react-i18next'
import {
  ILicenseItem,
  OfferItemsType,
  ProductAvailableType,
  SaleTarget,
} from 'api/productApi'
import useProductManagement from 'features/productManagement/useProductManagement'
import {format} from 'date-fns'
import {enUS, ko} from 'date-fns/locale'
import {
  DEFAULT_ONE_QUANTITY,
  PURCHASE_LIMIT,
  OffersType,
  PAYMENT_METHOD,
  PaymentMethod,
  PREPAYMENT_ITEM,
} from 'pages/ProductsPages/constant'
import ChargeProductDialog from 'components/Dialog/Product/ChargeProductDialog/ChargeProductDialog'
import useNoticeConfirm from 'features/modal/useNoticeConfirm'
import {navigate} from 'AppHistory'
import RouteConstant from 'constants/RouteConstant'
import {formatPriceNumber} from 'helpers/uiHelper'
import {useAppDispatch} from 'hooks'
import {
  openReportImageDialog,
  setReportDialogId,
} from 'features/modal/modalSlice'
import {PACKAGE_AVAILABLE_IMAGE_ID} from 'pages/ProductOrderPages/constants'

const useStyles = makeStyles(ProductsOrderStyle)

interface ProductInfoProps {
  Info: {
    goodsId: string
    goodsName: string
    description: string
    paymentMethod: string
    amountProduct: string
    amountOriginalProduct: string
    productSale: ProductAvailableType['sale']
    Duration: number
    canCharge: boolean
    offerType: OffersType
    offerItems: OfferItemsType[]
    saleTarget: SaleTarget
  }
  licenseItems?: ILicenseItem[]
  isOrdered?: boolean
  deactivateDate?: Date
  licenseId?: string
  isDuplicate?: boolean
  countPurchased?: number
}

const ProductInfo = ({
  Info,
  isOrdered = false,
  deactivateDate = new Date(),
  licenseId = '',
  isDuplicate = false,
  licenseItems = [],
  countPurchased = 0,
}: ProductInfoProps) => {
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const {getPackageName, getCLS, getOrder} = useProductManagement()
  const {onOpen: onNoticeDialogOpen} = useNoticeConfirm()
  const dispatch = useAppDispatch()

  const [isOpen, setIsOpen] = useState<boolean>(false)

  const isPurchaseAvailable = useMemo(() => {
    return Info.offerItems.some((item) => item.offerAmount > 0)
  }, [Info])

  const disabledButton = useMemo(() => {
    return (
      !isPurchaseAvailable ||
      !Info.offerItems.some((item) => item.offerAmount > 0)
    )
  }, [isPurchaseAvailable, Info.paymentMethod])

  const sortedOfferItems = useMemo(() => {
    const newOfferItems = [
      ...Info.offerItems.map((item) => {
        const related = licenseItems.filter(
          (lic) => lic.packageItemId === item.pkgItemId,
        )

        return {
          ...item,
          cls: getCLS(item.pkgItemId),
          packageName: getPackageName(item.pkgItemId),
          chargeAmount: related.reduce((acc, lic) => acc + lic.priority, 0),
          usageAmount: related.reduce((acc, lic) => acc + lic.usageQuantity, 0),
          order: getOrder(item.pkgItemId),
        }
      }),
    ]
    return newOfferItems.toSorted(
      (a, b) =>
        Number(b.offerAmount > 0) - Number(a.offerAmount > 0) ||
        a.cls.localeCompare(b.cls) ||
        a.order - b.order,
    )
  }, [Info.offerItems, i18n.language])

  const handleBuyProduct = () => {
    const productPath = `${RouteConstant.PRODUCTS_PAYMENT.path.replace(
      ':id',
      Info.goodsId,
    )}?type=purchase`

    if (countPurchased === DEFAULT_ONE_QUANTITY) {
      onNoticeDialogOpen({
        title: t('IInstructionsForUse'),
        message: t('IProductAvailableFrom', {
          date:
            i18n.language === 'ko'
              ? format(deactivateDate, 'yyyy년 MM월 dd일', {
                  locale: ko,
                })
              : format(deactivateDate, 'MMMM d, yyyy', {
                  locale: enUS,
                }),
        }),
      }).then((result) => {
        if (result.payload) {
          navigate(productPath)
        }
      })
    } else if (countPurchased === PURCHASE_LIMIT) {
      onNoticeDialogOpen({
        title: t('IInstructionsForUse'),
        message: t('IProductLimitedPurchase'),
      }).then((result) => {
        if (result.payload) {
          //
        }
      })
    } else {
      navigate(productPath)
    }
  }

  const displayPaymentMethod = useMemo(() => {
    if (Info.paymentMethod === PAYMENT_METHOD.PREPAYMENT) {
      if (Info.offerType === PREPAYMENT_ITEM.PROVIDER) {
        return t('IUpfrontPlanMethod')
      }
      if (Info.offerType === PREPAYMENT_ITEM.MONTHLY_OFFER) {
        return t('IMonthlyPlanMethod')
      }
    }
    return t('IManualPaid')
  }, [Info.paymentMethod, t])

  const [isOpenCharge, setIsOpenCharge] = useState<boolean>(false)

  const showReportImage = (packageId: string) => {
    if (PACKAGE_AVAILABLE_IMAGE_ID.includes(packageId)) {
      dispatch(setReportDialogId(packageId))
      dispatch(openReportImageDialog())
    }
  }

  return (
    <div className={classes.productCard}>
      <div className={classes.cardHeader}>
        {isOrdered && (
          <div className={classes.myBadge}>{t('IInOperation')}</div>
        )}
      </div>

      <div className={classes.cardContent}>
        <div>
          <div
            className={classes.productTitle}
            style={{width: `calc(100% - ${isOrdered ? 80 : 0}px)`}}
          >
            {Info.goodsName}
          </div>
          <p className={classes.productType}>{displayPaymentMethod}</p>
          <div className={classes.price}>
            {Info.amountProduct === Info.amountOriginalProduct ? (
              <div>{Info.amountProduct}</div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div>{Info.amountProduct}</div>
                <div
                  style={{
                    textDecoration: 'line-through',
                    opacity: 0.5,
                  }}
                >
                  {Info.amountOriginalProduct}
                </div>
              </div>
            )}
          </div>
          <div>
            {t('IDurationV2')} :{' '}
            <span className={classes.periodHighlight}>
              {Info.Duration > 10000000
                ? t('IUnlimited')
                : `${Info.Duration} ${t('IMonths')}`}
            </span>
          </div>
        </div>

        <Fragment>
          {sortedOfferItems.slice(0, 4).map((item, index: number) => (
            <div
              className={classes.specItem}
              key={`${item.pkgItemId}_${index}`}
            >
              <span
                className={
                  PACKAGE_AVAILABLE_IMAGE_ID.includes(item.pkgItemId)
                    ? classes.clickableSpecLabel
                    : classes.specLabel
                }
                onClick={() => showReportImage(item.pkgItemId)}
              >
                {item.packageName}
              </span>
              {Info.paymentMethod === PAYMENT_METHOD.PREPAYMENT && (
                <span className={classes.specValue}>
                  {item.offerAmount > 10000000
                    ? t('IUnlimited')
                    : `${formatPriceNumber(item.offerAmount)} ${
                        item.offerUnit === 'Count' ? t('ICase') : t('IMinute')
                      }`}
                </span>
              )}
            </div>
          ))}

          {Info.offerItems.length > 4 && (
            <Button
              className={classes.specItem}
              onClick={() => setIsOpen(true)}
            >
              {t('ISeeMore')}...
            </Button>
          )}
        </Fragment>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: 'auto',
            gap: 15,
          }}
        >
          <div className={classes.periodSection}>
            <div className={classes.periodText}>
              {isDuplicate && (
                <>
                  <span>
                    {t('INoticePurchase', {
                      time: format(deactivateDate, 'yyyy.MM.dd'),
                    })}
                  </span>
                  <br />
                </>
              )}
              <div
                style={{
                  wordWrap: 'break-word',
                }}
              >
                {t('IDescription')} : {Info.description}
              </div>
            </div>
          </div>

          <div className={classes.cardInfoBtns}>
            <Button
              color='primary'
              variant='contained'
              onClick={() => setIsOpen(true)}
            >
              {t('IAgreeToTerms')}
            </Button>
            {Info.canCharge && (
              <Button
                color='primary'
                variant='contained'
                onClick={() => setIsOpenCharge(true)}
                disabled={disabledButton}
                style={{
                  flex: ['en', 'ja'].includes(i18n.language) ? 0 : 1,
                }}
              >
                {t('IChargeV1')}
              </Button>
            )}
          </div>
        </div>
      </div>
      {isOpen && (
        <DetailProductOrderDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          typeCharge={Info.paymentMethod as PaymentMethod}
          totalAmount={Info.amountProduct}
          totalOriginalAmount={Info.amountOriginalProduct}
          offerType={Info.offerType}
          productName={Info.goodsName}
          paymentMethod={displayPaymentMethod}
          duration={Info.Duration}
          description={Info.description}
          goods={Info.offerItems.map((item) => {
            const related = licenseItems.filter(
              (lic) => lic.packageItemId === item.pkgItemId,
            )

            return {
              ...item,
              chargeAmount: related.reduce((acc, lic) => acc + lic.priority, 0),
              order: getOrder(item.pkgItemId),
              usageAmount: related.reduce(
                (acc, lic) => acc + lic.usageQuantity,
                0,
              ),
            }
          })}
          onBuyProduct={handleBuyProduct}
          canBuyProduct
        />
      )}
      {isOpenCharge && (
        <ChargeProductDialog
          open={isOpenCharge}
          productName={Info.goodsName}
          offerType={Info.offerType}
          typeCharge={Info.paymentMethod as PaymentMethod}
          paymentMethod={displayPaymentMethod}
          duration={Info.Duration}
          description={Info.description}
          onClose={() => setIsOpenCharge(false)}
          goods={Info.offerItems.map((item) => {
            const related = licenseItems.filter(
              (lic) => lic.packageItemId === item.pkgItemId,
            )

            return {
              ...item,
              offerAmount: related.reduce(
                (acc, lic) => acc + lic.offerQuantity,
                0,
              ),
              usageAmount: related.reduce(
                (acc, lic) => acc + lic.usageQuantity,
                0,
              ),
              order: getOrder(item.pkgItemId),
            }
          })}
          goodsId={Info.goodsId}
          licenseId={licenseId}
          saleTarget={Info.saleTarget}
          productSale={Info.productSale}
        />
      )}
    </div>
  )
}

export default ProductInfo
